import { Model } from '@contember/schema';
import { DefaultNamingConventions, emptySchema, isInverseRelation, isRelation } from '@contember/schema-utils';
import { InverseEntityField, RootEntityField, RootEntityName } from '@contember-cloud/studio-plugins';
import { camelCase } from 'change-case';
import { emptySchemaExtensions, formatPluginName } from '../common';
import { isFieldType, SchemaBuilder } from './builder/SchemaBuilder';
import { SchemaPartsFactory } from './builder/SchemaPartsFactory';
import { createSchemaDslParser } from './createSchemaDslParser';
import { SchemaDslBuilder } from './SchemaDslBuilder';
import { statementSchemaHandlerMap } from './stmtsList';
var RelationType = Model.RelationType;
export const buildSchemaFromDsl = (input, plugins = []) => {
    const parser = createSchemaDslParser();
    const parserResult = parser.parse(input);
    const statements = parserResult.statements;
    const schemaBuild = buildSchemaFromStatements(statements, plugins);
    schemaBuild.errors.push(...parserResult.errors);
    return schemaBuild;
};
export const buildSchemaFromStatements = (statements, plugins) => {
    const builder = new SchemaDslBuilder(emptySchema, emptySchemaExtensions, statementSchemaHandlerMap(buildPlugins(plugins)));
    const applyResult = builder.applyStatements(statements);
    return {
        errors: [...applyResult.errors],
        schema: applyResult.schema,
        humanReadableStatements: applyResult.humanReadableStatements,
        statements: statements,
    };
};
const buildPlugins = (plugins) => {
    return plugins?.reduce((acc, plugin) => {
        acc[formatPluginName(plugin.slug)] = buildHandlerFromPlugin(plugin);
        return acc;
    }, {});
};
const getPluginTargetEntityName = (plugin) => {
    switch (plugin.type) {
        case 'FieldAttached': {
            const rootEntity = plugin.schema?.model.entities[RootEntityName];
            const rootEntityField = rootEntity?.fields[RootEntityField];
            if (rootEntityField && isRelation(rootEntityField)) {
                return rootEntityField.target;
            }
            return '';
        }
        default:
            console.error(`Unsupported plugin type: ${plugin.type}`);
            return '';
    }
};
export const buildHandlerFromPlugin = (plugin) => {
    const namingConventions = new DefaultNamingConventions();
    const schemaParts = new SchemaPartsFactory(namingConventions);
    switch (plugin.type) {
        case 'Basic': {
            return {
                processPlugin: ({ stmt, schemaBuilder }) => {
                    if (!plugin.schema)
                        return schemaBuilder;
                    const pluginSchemaBuilder = new SchemaBuilder(plugin.schema, schemaParts, namingConventions, {});
                    const pluginSchema = pluginSchemaBuilder.updateEntityName(RootEntityName, stmt.entityName, { renameTable: true }).schema;
                    return schemaBuilder.mergeEnums(pluginSchema.model.enums).appendEntities(pluginSchema.model.entities);
                },
                schema: plugin.schema,
                type: plugin.type,
                handlerArgs: JSON.parse(plugin.handlerArgs ?? '{}'),
            };
        }
        case 'FieldAttached':
            return {
                processPlugin: ({ stmt, schemaBuilder }) => {
                    if (!plugin.schema)
                        return schemaBuilder;
                    const fieldName = stmt.fieldName;
                    const targetEntity = getPluginTargetEntityName(plugin);
                    const pluginFieldName = camelCase(`${stmt.entityName} ${stmt.fieldName}`);
                    const pluginSchemaBuilder = new SchemaBuilder(plugin.schema, schemaParts, namingConventions, {});
                    let pluginSchema;
                    if (fieldName) {
                        pluginSchema = pluginSchemaBuilder
                            .updateEntityName(RootEntityName, stmt.entityName, { renameTable: true })
                            .updateEntity(stmt.entityName, ({ entity }) => {
                            return {
                                ...entity,
                                tableName: namingConventions.getTableName(stmt.entityName),
                            };
                        })
                            .updateFieldName(stmt.entityName, RootEntityField, fieldName)
                            .updateField(stmt.entityName, fieldName, isFieldType(['oneHasOneOwning', 'oneHasOneInverse', 'oneHasMany', 'manyHasOne', 'manyHasManyOwning', 'manyHasManyInverse']), ({ field }) => {
                            if (isInverseRelation(field)) {
                                return {
                                    ...field,
                                    ownedBy: pluginFieldName,
                                };
                            }
                            if (field.type === RelationType.ManyHasMany) {
                                return {
                                    ...field,
                                    inversedBy: pluginFieldName,
                                    joiningTable: {
                                        ...field.joiningTable,
                                        tableName: namingConventions.getJoiningTableName(stmt.entityName, fieldName),
                                        joiningColumn: {
                                            ...field.joiningTable.joiningColumn,
                                            columnName: namingConventions.getJoiningColumnName(stmt.entityName),
                                        },
                                    },
                                };
                            }
                            return {
                                ...field,
                                joiningColumn: {
                                    ...field.joiningColumn,
                                    columnName: namingConventions.getJoiningColumnName(fieldName),
                                },
                                inversedBy: pluginFieldName,
                            };
                        })
                            .updateFieldName(targetEntity, InverseEntityField, pluginFieldName)
                            .updateField(targetEntity, pluginFieldName, isFieldType(['oneHasOneOwning', 'oneHasOneInverse', 'oneHasMany', 'manyHasOne', 'manyHasManyOwning', 'manyHasManyInverse']), ({ field }) => {
                            if (isInverseRelation(field)) {
                                return {
                                    ...field,
                                };
                            }
                            if (field.type === RelationType.ManyHasMany) {
                                return {
                                    ...field,
                                    joiningTable: {
                                        ...field.joiningTable,
                                        tableName: namingConventions.getJoiningTableName(targetEntity, pluginFieldName),
                                        joiningColumn: {
                                            ...field.joiningTable.joiningColumn,
                                            columnName: namingConventions.getJoiningColumnName(targetEntity),
                                        },
                                    },
                                };
                            }
                            return {
                                ...field,
                                joiningColumn: {
                                    ...field.joiningColumn,
                                    columnName: namingConventions.getJoiningColumnName(pluginFieldName),
                                },
                            };
                        }).schema;
                    }
                    else {
                        pluginSchema = pluginSchemaBuilder
                            .updateEntityName(RootEntityName, stmt.entityName)
                            .updateEntity(stmt.entityName, ({ entity }) => ({
                            ...entity,
                            tableName: namingConventions.getTableName(stmt.entityName),
                        }))
                            .updateFieldName(targetEntity, InverseEntityField, pluginFieldName).schema;
                    }
                    return schemaBuilder.mergeEnums(pluginSchema.model.enums).appendEntities(pluginSchema.model.entities);
                },
                schema: plugin.schema,
                type: plugin.type,
                handlerArgs: JSON.parse(plugin.handlerArgs ?? '{}'),
            };
        default:
            console.error(`Unsupported plugin: ${JSON.stringify(plugin)}`);
            return {
                processPlugin: ({ schemaBuilder }) => schemaBuilder,
                type: undefined,
                schema: emptySchema,
            };
    }
};
//# sourceMappingURL=buildSchemaFromSource.js.map